import { useEffect, lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { BitrixProvider } from "@/providers/BitrixProvider";
import { QueryProvider } from "@/providers/QueryProvider";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { AppShell } from "@/components/layout/AppShell";
import { PageLoader } from "@/components/ui/PageLoader";
import { initWebMCP } from "@/lib/webmcp";

// Initialize i18n (must run before any component renders)
import "@i18n";

// Route-level code splitting -- each feature chunk is fetched on first navigation
const DashboardView = lazy(() =>
  import("@/features/dashboard/DashboardView").then((m) => ({ default: m.DashboardView })),
);
const RuleListView = lazy(() =>
  import("@/features/rules/RuleListView").then((m) => ({ default: m.RuleListView })),
);
const RuleBuilderPage = lazy(() =>
  import("@/features/rules/RuleBuilderPage").then((m) => ({ default: m.RuleBuilderPage })),
);
const TemplatesDirectory = lazy(() =>
  import("@/features/rules/templates/TemplatesDirectory").then((m) => ({
    default: m.TemplatesDirectory,
  })),
);

const CommissionListView = lazy(() =>
  import("@/features/commissions/CommissionListView").then((m) => ({
    default: m.CommissionListView,
  })),
);
const CommissionDetailView = lazy(() =>
  import("@/features/commissions/CommissionDetailView").then((m) => ({
    default: m.CommissionDetailView,
  })),
);
const SettingsView = lazy(() =>
  import("@/features/settings/SettingsView").then((m) => ({ default: m.SettingsView })),
);
const HelpView = lazy(() =>
  import("@/features/help/HelpView").then((m) => ({ default: m.HelpView })),
);
const EventLogView = lazy(() =>
  import("@/features/events/EventLogView").then((m) => ({ default: m.EventLogView })),
);

const suspenseFallback = <PageLoader />;

// --- Data router (required for useBlocker in RuleBuilderPage) -----------------
// Providers wrap RouterProvider - route components can still access all contexts
// (Zustand, TanStack Query, theme, B24 SDK) because React context propagates down.
const router = createBrowserRouter(
  [
    {
      element: <AppShell />,
      children: [
        { path: "/dashboard", element: <DashboardView /> },
        { path: "/rules", element: <RuleListView /> },
        { path: "/rules/templates", element: <TemplatesDirectory /> },
        { path: "/rules/new", element: <RuleBuilderPage /> },
        { path: "/rules/:id/edit", element: <RuleBuilderPage /> },
        { path: "/commissions", element: <CommissionListView /> },
        { path: "/commissions/:id", element: <CommissionDetailView /> },
        { path: "/settings", element: <SettingsView /> },
        { path: "/events", element: <EventLogView /> },
        { path: "/help", element: <HelpView /> },
        { path: "/handler", element: <Navigate to="/dashboard" replace /> },
        { path: "*", element: <Navigate to="/dashboard" replace /> },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);

// ===========================================================
// App - Root component with providers + data router (TPP 5.1)
// ===========================================================

export default function App() {
  useEffect(() => {
    initWebMCP();
  }, []);

  return (
    <BitrixProvider>
      <QueryProvider>
        <ThemeProvider>
          <Suspense fallback={suspenseFallback}>
            <RouterProvider router={router} />
          </Suspense>
        </ThemeProvider>
      </QueryProvider>
    </BitrixProvider>
  );
}
